import { lazy, Suspense, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const TaskModal = lazy(() => import("@/pages/tracker/Tasks").then((m) => ({ default: m.TaskModal })));

let emit = null;

// Open the full task edit form over the task window (sharing its backdrop); onSaved gets the fresh task.
export function openTaskEditor(task, onSaved, onClosed) {
  if (emit) emit({ task, onSaved, onClosed });
}

// Open the same form to create a new task, pre-filled (e.g. from a private to-do); onCreated gets the task.
export function openTaskCreator(prefill, onCreated) {
  if (emit) emit({ task: null, prefill, onSaved: onCreated });
}

export function TaskEditorHost() {
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    emit = setReq;
    return () => { emit = null; };
  }, []);

  useEffect(() => {
    if (!req) return;
    Promise.all([
      api.get("/projects"),
      api.get("/users/directory"),
      api.get("/tasks", { params: req.task ? { project_id: req.task.project_id } : {} }),
    ])
      .then(([p, u, t]) => {
        const manageable = user.role === "admin" ? p.data : p.data.filter((pr) =>
          (pr.lead_ids || []).includes(user.id) || (pr.groups || []).some((g) => (g.lead_ids || []).includes(user.id)));
        setData({ req, projects: manageable, users: u.data, tasks: t.data });
      })
      .catch((e) => { toast.error(formatApiError(e)); req.onClosed?.(); setReq(null); });
  }, [req, user]);

  const saved = async (created) => {
    if (!req.task) {
      if (created) req.onSaved?.(created);
      return;
    }
    try {
      const { data: fresh } = await api.get(`/tasks/${req.task.id}`);
      req.onSaved?.(fresh);
    } catch {
      req.onSaved?.(null);
    }
  };

  // Only render with data fetched for this request, never a previous one.
  if (!user || !req || data?.req !== req) return null;
  return (
    <div className="relative z-[60]">
      <Suspense fallback={null}>
        <TaskModal
          open
          onClose={() => { req.onClosed?.(); setReq(null); }}
          onSaved={saved}
          editing={req.task}
          projects={data.projects}
          users={data.users}
          tasks={data.tasks}
          user={user}
          stacked={!!req.task}
          prefill={req.prefill}
        />
      </Suspense>
    </div>
  );
}
